/**
 * Toasts render above everything (the toast library pins its region to the maximum z-index), so an
 * overlay sharing the bottom-right corner is covered rather than competing. Anything occupying that
 * corner registers its size here and toasts move clear of it.
 *
 * Which axis to move on depends on the overlay's shape. A full-height side drawer leaves no room
 * below it, so toasts shift left of it. The upload queue is a short card, so toasts stack on top of
 * it and both stay readable.
 *
 * The offsets travel as CSS variables because the toast region's insets are written as inline styles
 * when the toaster is created, which no class-based style prop can override. A variable inside those
 * inline `calc()` values stays late-bound.
 */

export type ToastInsetAxis = 'right' | 'bottom';

export const TOAST_INSET_VARS: Record<ToastInsetAxis, string> = {
	right: '--toast-inset-right',
	bottom: '--toast-inset-bottom',
};

/** Breathing room between a toast and the viewport edge, and between a toast and what it clears. */
export const TOAST_EDGE_INSET_PX = 16;

/** Toasts and the upload queue share a width so a stacked pair reads as one column, not two cards. */
export const OVERLAY_CORNER_WIDTH_PX = 420;

/**
 * Drawer widths arrive as the CSS length their author wrote. Anything not already in pixels (a
 * token, a percentage, a responsive object) has no width until it is laid out, so the caller falls
 * back rather than guessing.
 */
export const cssLengthToPx = (length: string | number): number | null => {
	if (typeof length === 'number') return length;

	const pixels = /^(\d+(?:\.\d+)?)px$/.exec(length.trim());
	return pixels ? Number(pixels[1]) : null;
};

const obstructionsByAxis: Record<ToastInsetAxis, Map<string, number>> = {
	right: new Map(),
	bottom: new Map(),
};

const applyLargestObstruction = (axis: ToastInsetAxis): void => {
	const sizes = [...obstructionsByAxis[axis].values()];
	const largest = sizes.length > 0 ? Math.max(...sizes) : TOAST_EDGE_INSET_PX;
	document.documentElement.style.setProperty(TOAST_INSET_VARS[axis], `${largest}px`);
};

/**
 * Keyed so overlapping owners on one axis settle on the largest rather than on whichever rendered
 * last.
 */
export const registerToastObstruction = (axis: ToastInsetAxis, id: string, sizePx: number): void => {
	obstructionsByAxis[axis].set(id, sizePx);
	applyLargestObstruction(axis);
};

export const clearToastObstruction = (axis: ToastInsetAxis, id: string): void => {
	obstructionsByAxis[axis].delete(id);
	applyLargestObstruction(axis);
};
